// --------------------------------------------------------------
// Created On: 2025-07-16
// Author: Dimitra Weinstein
//
// Last Modified: 2025-10-27
// Modified By: Zachary Thomas
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------
import React, { useState, useEffect, Fragment } from "react";
import deepCopy from "src/utilities/deepCopy";
import { MAX_RESULTS_PER_PAGE } from "src/constants/miscellaneous";
import SortedTableHeader from "src/components/SortedTableHeader/SortedTableHeader";
import styles from "./SearchDeviceList.module.scss";
import PaginationControl from "src/components/PaginationControl/PaginationControl";
import SearchDeviceListRow from "./SearchDeviceListRow/SearchDeviceListRow";
import SubscriptionModal from "./SubscriptionModal/SubscriptionModal";
import DeviceStatusModal from "src/components/DeviceStatusModal/DeviceStatusModal";

// A list of monitoring devices that were the result of a search.
export default function SearchDeviceList(props: Props): Component {
  const initialDevice: DeviceWithStatus = {
    deviceId: 0,
    deviceType: "",
    identifier: "",
    assetName: "",
    subscriptionType: "",
    activationStatus: "",
    subscriptionStartDate: "",
    subscriptionExpiry: "",
    subscriptionNumber: "",
    statuses: [],
  };
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [devices, setDevices] = useState<DeviceWithStatus[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<DeviceWithStatus>(initialDevice);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState<boolean>(false);
  const [showStatusModal, setShowStatusModal] = useState<boolean>(false);

  // Get the devices that exist just on the current page.
  useEffect(() => {
    const startingIndex = pageNumber * MAX_RESULTS_PER_PAGE - MAX_RESULTS_PER_PAGE;
    let endingIndex = pageNumber * MAX_RESULTS_PER_PAGE;
    if (endingIndex >= props.devices.length) {
      endingIndex = props.devices.length;
    }
    const formattedDevices = formatDevices(props.devices, props.deviceStatuses);
    const devicesDeepCopy = deepCopy(formattedDevices);
    const sortedDevices: DeviceWithStatus[] = sortDevices(devicesDeepCopy);
    setDevices(sortedDevices.slice(startingIndex, endingIndex));
  }, [pageNumber, JSON.stringify(props.devices), props.sortAscending, props.sortColumnIndex]);

  // If the search results change, reset the selected page to page one.
  useEffect(() => {
    setPageNumber(1);
  }, [JSON.stringify(props.devices)]);

  // If the results change to/from cross company, change the starting sort index.
  useEffect(() => {
    if (props.crossCompany) {
      props.onSortColumnIndexChange(1);
      props.onSortAscendingChange(false);
    } else {
      props.onSortColumnIndexChange(2);
      props.onSortAscendingChange(false);
    }
  }, [props.crossCompany]);

  // Format devices to include status names, values, and descriptions.
  function formatDevices(devices: Device[], deviceStatusLookUps: DeviceStatus[]): DeviceWithStatus[] {
    const devicesDeepCopy = deepCopy(devices);
    devicesDeepCopy.forEach((device) => {
      device.statuses.forEach((deviceStatus, i) => {
        const statusLookUp = deviceStatusLookUps.find(
          (statusLookUp) => statusLookUp.deviceStatusId === deviceStatus.deviceStatusId
        );
        if (statusLookUp !== undefined) {
          const status = {
            deviceStatusId: deviceStatus.deviceStatusId,
            value: deviceStatus.value,
            name: statusLookUp.name,
            code: statusLookUp.code,
            description: statusLookUp.description,
          };
          device.statuses[i] = status;
        }
      });
    });
    return devicesDeepCopy as DeviceWithStatus[];
  }

  // Format device statuses to include names, values, and descriptions.
  // This is used to display the status information in the status modal.
  function formatDeviceStatuses(device: Device, deviceStatusLookUps: DeviceStatus[]): DeviceStatus[] {
    const statuses: DeviceStatus[] = [];
    device.statuses.forEach((deviceStatus) => {
      const statusLookUp = deviceStatusLookUps.find(
        (statusLookUp) => statusLookUp.deviceStatusId === deviceStatus.deviceStatusId
      );
      if (statusLookUp !== undefined) {
        const status = {
          deviceStatusId: deviceStatus.deviceStatusId,
          value: deviceStatus.value,
          name: statusLookUp.name,
          code: statusLookUp.code,
          description: statusLookUp.description,
        };
        statuses.push(status);
      }
    });
    return statuses;
  }

  // Sort device search results based on the current column and ascension rule.
  function sortDevices(devices: DeviceWithStatus[]): DeviceWithStatus[] {
    return devices.sort((a, b) => {
      let aValue = "";
      let bValue = "";

      switch (props.sortColumnIndex) {
        case 1:
          aValue = a.identifier;
          bValue = b.identifier;
          break;
        case 2:
          aValue = a.deviceType;
          bValue = b.deviceType;
          break;
        case 3:
          aValue = a.activationStatus || "";
          bValue = b.activationStatus || "";
          break;
        case 4:
          aValue = a.assetName || "";
          bValue = b.assetName || "";
          break;
        default:
          aValue = a.identifier;
          bValue = b.identifier;
      }

      if (aValue < bValue) {
        if (props.sortAscending) {
          return -1;
        } else {
          return 1;
        }
      } else if (aValue > bValue) {
        if (props.sortAscending) {
          return 1;
        } else {
          return -1;
        }
      } else {
        return 0;
      }
    });
  }

  return !props.loading ? (
    <Fragment>
      {props.devices.length > 0 ? (
        <div>
          <div className="table-responsive">
            <table className={`${styles.table} table mb-0 pb-0`}>
              <thead className={styles.thead}>
                <tr>
                  <SortedTableHeader
                    name="Device Identifier"
                    data-test="search-monitoring-device-type-header"
                    index={1}
                    selectedIndex={props.sortColumnIndex}
                    selectedAscending={props.sortAscending}
                    onClick={(index) => props.onUpdateSort(index)}
                  />
                  <SortedTableHeader
                    name="Device Type"
                    index={2}
                    selectedIndex={props.sortColumnIndex}
                    selectedAscending={props.sortAscending}
                    onClick={(index) => props.onUpdateSort(index)}
                  />
                  <SortedTableHeader
                    name="Activation Status"
                    index={3}
                    selectedIndex={props.sortColumnIndex}
                    selectedAscending={props.sortAscending}
                    className="d-none d-md-table-cell"
                    onClick={(index) => props.onUpdateSort(index)}
                  />
                  <SortedTableHeader
                    name="Asset"
                    index={4}
                    selectedIndex={props.sortColumnIndex}
                    selectedAscending={props.sortAscending}
                    className="d-none d-md-table-cell"
                    onClick={(index) => props.onUpdateSort(index)}
                  />
                  <th className={`${styles.unsortedColumn} d-none d-md-table-cell`}>Subscription Info</th>
                  <th className={`${styles.unsortedColumn} d-none d-md-table-cell`}>Status Info</th>
                </tr>
              </thead>
              <tbody>
                {devices.map((device, i) => (
                  <SearchDeviceListRow
                    key={i}
                    deviceId={device.deviceId}
                    deviceType={device.deviceType}
                    deviceIdentifier={device.identifier}
                    assetName={device.assetName}
                    subscriptionType={device.subscriptionType}
                    subscriptionStartDate={device.subscriptionStartDate}
                    subscriptionExpiry={device.subscriptionExpiry}
                    subscriptionNumber={device.subscriptionNumber}
                    activationStatus={device.activationStatus !== null ? device.activationStatus : "N/A"}
                    statuses={device.statuses}
                    deviceStatuses={device.statuses}
                    onShowStatusModal={() => {
                      setSelectedDevice(device), setShowStatusModal(true);
                    }}
                    onShowSubscriptionModal={() => {
                      setSelectedDevice(device), setShowSubscriptionModal(true);
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <PaginationControl
            pageNumber={pageNumber}
            maxPerPage={MAX_RESULTS_PER_PAGE}
            totalCount={props.devices.length}
            onClick={(pageNumber) => setPageNumber(pageNumber)}
          />
          {/* SubscriptionModal */}
          <SubscriptionModal
            showSubscriptionModal={showSubscriptionModal}
            deviceId={selectedDevice.deviceId}
            deviceType={selectedDevice.deviceType}
            deviceIdentifier={selectedDevice.identifier}
            subscriptionType={selectedDevice.subscriptionType}
            activationStatus={selectedDevice.activationStatus !== null ? selectedDevice.activationStatus : "N/A"}
            subscriptionNumber={selectedDevice.subscriptionNumber}
            subscriptionStartDate={selectedDevice.subscriptionStartDate}
            subscriptionExpiry={selectedDevice.subscriptionExpiry}
            onCloseSubscriptionModal={() => setShowSubscriptionModal(false)}
          />

          {/* StatusModal */}
          <DeviceStatusModal
            showModal={showStatusModal}
            deviceId={selectedDevice.deviceId}
            deviceIdentifier={selectedDevice.identifier}
            deviceType={selectedDevice.deviceType}
            statuses={formatDeviceStatuses(selectedDevice, props.deviceStatuses)}
            onUpdateStatus={() => {
              /* Do nothing. */
            }}
            onClose={() => setShowStatusModal(false)}
          />
        </div>
      ) : (
        <div className={`${styles.emptyList} py-5 px-4`}>
          <h5 className="my-0">No devices found</h5>
        </div>
      )}
    </Fragment>
  ) : (
    <div className={`${styles.emptyList} py-5 px-4`}>
      <h5 className="my-0">Searching...</h5>
    </div>
  );
}

interface Props {
  loading: boolean;
  sortColumnIndex: number;
  sortAscending: boolean;
  devices: Device[];
  deviceStatuses: DeviceStatus[];
  crossCompany: boolean;
  onUpdateSort: (index: number) => void;
  onSortAscendingChange: (ascending: boolean) => void;
  onSortColumnIndexChange: (index: number) => void;
}

interface Device {
  deviceId: number;
  deviceType: string;
  identifier: string;
  assetName: string;
  subscriptionType: string;
  activationStatus: string;
  subscriptionStartDate: string;
  subscriptionExpiry: string;
  subscriptionNumber: string;
  statuses: Status[];
}

interface Status {
  deviceStatusId: number;
  value: string;
}

interface DeviceStatus {
  code: string;
  description: string;
  deviceStatusId: number;
  name: string;
  value: string;
}

interface DeviceWithStatus {
  deviceId: number;
  deviceType: string;
  identifier: string;
  assetName: string;
  subscriptionType: string;
  activationStatus: string;
  subscriptionStartDate: string;
  subscriptionExpiry: string;
  subscriptionNumber: string;
  statuses: DeviceStatus[];
}
